import { Box, Button, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import useMSSP from "../../../../../api/hooks/useMSSP";
import ExportButton from "../../../../../components/buttons/ExportButton";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import { QueryParam } from "../../../../../utils/constants";
import ColumnSettingsModal, {
  getVisibleColumnsForEntity,
} from "../../components/ColumnSettingsModal";
import EditLabelsButton from "../../components/EditLabelsButton";
import EditTagsButton from "../../components/EditTagsButton";
import EntityDrawer from "../../components/EntityDrawer/EntityDrawer";
import MarkAsCriticalButton from "../../components/MarkAsCriticalButton/MarkAsCriticalButton";
import SearchBox from "../../components/SearchBox";
import RdsEntitiesTable from "../../entitiesTable/RdsEntitiesTable";
import useEntitiesExport from "../../hooks/useEntitiesExport";
import useRdsEntitiesInfinite from "../../hooks/useRdsEntitiesInfinite";
import useRdsEntityById from "../../hooks/useRdsEntityById";

export const ALL_ENTITIES = "all_entities";

export default function RdsEntitiesOverviewTableView({
  assets,
  visibleColumns,
  setVisibleColumns,
  filtersPayload,
}) {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const { exportEntities } = useEntitiesExport();
  const { hasMultipleSelectedTenants } = useMSSP();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [checkedEntityIDs, setCheckedEntityIDs] = useState([]);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [sortModel, setSortModel] = useState([
    { field: "risk", sort: "desc" },
    { field: "display_name", sort: "asc" },
  ]);

  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const handleOpenColumnSettings = () => {
    trackEvent("User opened entity column settings");
    setColumnSettingsOpen(true);
  };

  const handleColumnsChange = (newSelectedColumns) => {
    setVisibleColumns(newSelectedColumns);
    trackEvent("User updated entity column visibility");
  };

  useEffect(() => {
    if (!isActiveRds) return;
    const savedColumns = getVisibleColumnsForEntity(ALL_ENTITIES);
    setVisibleColumns(savedColumns);
  }, [isActiveRds, setVisibleColumns]);

  const rdsQuery = useRdsEntitiesInfinite({
    entityType: "all",
    sortModel,
    visibleColumns,
    filtersPayload,
    freetextQuery: activeSearch,
    enabled: isActiveRds,
    queryKeyPrefix: "rds-entities-overview",
  });

  const rows = rdsQuery.data?.entities || [];
  const totalCount = rdsQuery.data?.totalCount || 0;
  const hasLoadedOnce = Boolean(rdsQuery.data);

  const viewedEntityUniqueId = decodeURIComponent(
    searchParams.get(QueryParam.entity) || ""
  );
  const entityFromTable = viewedEntityUniqueId
    ? rows.find((row) => row.unique_id === viewedEntityUniqueId)
    : null;

  const {
    data: fetchedEntityRds,
    isLoading: entityDetailsIsLoading,
    isFetching: entityDetailsIsFetching,
  } = useRdsEntityById({
    entityId: entityFromTable?.entity_id,
    customerId: entityFromTable?.customer_id,
    enabled: Boolean(
      isActiveRds && entityFromTable?.entity_id && entityFromTable?.customer_id
    ),
  });

  const viewedEntity = fetchedEntityRds || entityFromTable;

  const onDrawerClose = () => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.delete(QueryParam.entity);
    setSearchParams(updatedSearchParams);
    trackEvent("User closed the entity drawer");
  };

  const checkedEntities = rows.filter((entity) =>
    checkedEntityIDs.includes(entity.entity_id + "-" + entity.customer_id)
  );

  const handleTableExport = async () => {
    trackEvent("User click on 'Export' button to download the inventory table");
    setIsExporting(true);
    try {
      await exportEntities({
        filters: filtersPayload?.filters || null,
        filter_logic: filtersPayload?.filter_logic || null,
        entity_type: null,
        columns: visibleColumns,
        sort_by: sortModel?.[0]?.field || null,
        sort_dir: sortModel?.[0]?.sort || null,
      });
    } finally {
      setIsExporting(false);
    }
  };
  const checkedEntitiesIds = checkedEntities.map((entity) => entity.entity_id);

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <SearchBox
              colors={colors}
              placeholder="Search all entities..."
              searchInput={searchInput}
              onSearchInputChange={setSearchInput}
              activeSearch={activeSearch}
              onActiveSearchChange={setActiveSearch}
              minSearchLength={3}
              rightContent={
                hasLoadedOnce ? (
                  <Typography variant="body2" color="text.secondary">
                    Showing {rows.length.toLocaleString()} of{" "}
                    {totalCount.toLocaleString()} entities
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Loading entities…
                  </Typography>
                )
              }
            />
          </Box>

          <Box display="flex" gap={1}>
            {!hasMultipleSelectedTenants && (
              <>
                <EditLabelsButton checkedEntities={checkedEntities} />
                <EditTagsButton checkedEntities={checkedEntities} />
                <MarkAsCriticalButton
                  checkedEntityIDs={checkedEntitiesIds}
                  assets={assets}
                />
              </>
            )}
            <Button
              onClick={handleOpenColumnSettings}
              sx={{
                color: colors.text_primary,
                height: "30px",
                border: "1px solid " + colors.grey_48percent,
                textTransform: "none",
                borderRadius: "10px",
                cursor: "pointer",
                background: "white",
              }}
            >
              <Typography variant="overline">Columns</Typography>
            </Button>
            <ExportButton
              handleExport={handleTableExport}
              sx={{
                border: "1px solid " + colors.grey_48percent,
                background: "white",
              }}
              disabled={isExporting}
            />
          </Box>
        </Box>

        <RdsEntitiesTable
          entityType={ALL_ENTITIES}
          rows={rows}
          assets={assets}
          visibleColumns={visibleColumns}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
          }}
          onLoadMore={() => rdsQuery.fetchNextPage()}
          hasMore={rdsQuery.hasNextPage}
          isLoading={rdsQuery.isLoading}
          isFetching={rdsQuery.isFetching}
          isLoadingMore={rdsQuery.isFetchingNextPage}
          totalCount={totalCount}
          setCheckedEntityIDs={setCheckedEntityIDs}
          sx={{
            '& .MuiDataGrid-columnHeader[data-field="__check__"]': {
              width: "80px !important",
              minWidth: "80px !important",
              maxWidth: "80px !important",
            },
            '& .MuiDataGrid-cell[data-field="__check__"]': {
              width: "80px !important",
              minWidth: "80px !important",
              maxWidth: "80px !important",
            },
          }}
        />
      </Box>

      <ColumnSettingsModal
        entities={rows}
        open={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        entityType={ALL_ENTITIES}
        onColumnsChange={handleColumnsChange}
      />

      <EntityDrawer
        openDrawer={viewedEntity}
        selectedEntity={viewedEntity}
        entityDetailsIsLoading={
          Boolean(
            isActiveRds &&
              entityFromTable?.entity_id &&
              entityFromTable?.customer_id
          ) &&
          (entityDetailsIsLoading || entityDetailsIsFetching)
        }
        onClose={onDrawerClose}
      />
    </>
  );
}
